/**
 * Decode a JWT token without verification (client-side only)
 * Returns the payload or null if invalid
 */
export const decodeJwt = (token: string): { exp?: number; [key: string]: unknown } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Add padding for base64url (JWTs omit padding)
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Check if a token is expired or will expire within the buffer time
 * @param token JWT token string
 * @param bufferSeconds Seconds before actual expiration to consider it expired (default 60)
 * @returns true if expired or near expiry, false if valid, null if can't determine
 */
export const isTokenExpired = (token: string | null, bufferSeconds = 60): boolean | null => {
  if (!token) return true;

  const decoded = decodeJwt(token);
  if (!decoded || typeof decoded.exp !== 'number') {
    return null; // Can't determine, need to validate with backend
  }

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp - bufferSeconds <= now;
};
